import React from "react";
import { COMPANY } from "@/lib/site-data";
import { Phone, ArrowRight, Lightning, Star } from "@phosphor-icons/react";
import { HOMESTARS_URL } from "@/components/site/HomeStarsBadge";

/**
 * Final pre-footer CTA banner. Big, bold, conversion-focused.
 * Placed after FAQ so it catches scrollers who've consumed all info
 * but haven't filled the form yet.
 */
export default function FinalCta() {
  return (
    <section
      data-testid="final-cta-section"
      className="relative bg-slate-900 text-white overflow-hidden border-b-2 border-slate-900"
    >
      <div className="bg-grid-dark absolute inset-0 opacity-50 pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-sky-500 rotate-12 opacity-90" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 border border-sky-400 bg-sky-500/10 px-3 py-1.5 mb-6">
            <Lightning size={14} weight="fill" className="text-sky-400" />
            <span className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-sky-300">
              Same-day slots fill fast
            </span>
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Need a bin today?
            <br />
            <span className="text-sky-400">Book in under 60 seconds.</span>
          </h2>
          <p className="mt-5 text-slate-300 text-lg leading-relaxed max-w-xl">
            Most quote requests get a callback in under an hour. Tell us the job — we&apos;ll match the
            right bin and lock in your delivery window.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="/book"
              data-testid="final-cta-quote"
              className="group inline-flex items-center justify-center gap-3 px-7 py-4 bg-sky-500 hover:bg-sky-600 text-white font-bold uppercase tracking-[0.14em] text-sm border-2 border-sky-500 hover:border-sky-600 transition-colors"
            >
              Book your bin now
              <ArrowRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="final-cta-call"
              className="inline-flex items-center justify-center gap-3 px-7 py-4 border-2 border-white text-white font-bold uppercase tracking-[0.14em] text-sm hover:bg-white hover:text-slate-900 transition-colors"
            >
              <Phone size={20} weight="bold" />
              Call {COMPANY.phone}
            </a>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="border-2 border-sky-400 bg-slate-900/60 backdrop-blur p-7">
            <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-emerald-400">
              Why book Troya Mini Bins
            </div>
            <ul className="mt-5 space-y-4 text-sm">
              {[
                { t: "Same-day delivery in Toronto & GTA" },
                { t: "Transparent pricing — what we quote is what you pay" },
                { t: "Honest weighing on certified scales" },
                { t: "Friendly, polite drivers" },
              ].map((i, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="h-1.5 w-1.5 mt-2 bg-sky-400 shrink-0" />
                  <span className="text-slate-200">{i.t}</span>
                </li>
              ))}
            </ul>
            <a
              href={HOMESTARS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 font-semibold text-sm"
            >
              <Star size={14} weight="fill" className="text-amber-400" />
              5.0 ★ · 7 verified reviews on HomeStars →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
